import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import DataTable from "@/components/core/data-table";
import { Input } from "@/components/core/forms/input";
import { useSalesContractsQuery } from "@/features/dashboard/api/queries/sales-contract-query";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import { exportSalesContractsToPDF, exportToCSV, exportToXLSX, prepareSalesContractDataForExport } from "@/utils/export-data";
import { formatNumber } from "@/utils/format-number";
import { IconFileExport, IconFileSpreadsheet, IconFileTypeCsv, IconFileTypePdf, IconSearch, IconX } from "@tabler/icons-react";
import { format, formatDate } from "date-fns";
import type React from "react";
import { useEffect, useState } from "react";

interface AdminSearchProps {
	className?: string;
	onSearchChange?: (searchTerm: string) => void;
	onSearchResults?: (results: Record<string, unknown>) => void;
	onUpcomingEndDatesResults?: (results: Record<string, unknown>) => void;
	dateFilter?: string;
	dateFrom?: string;
	dateTo?: string;
	clearSearch?: boolean;
	salespersonId?: number;
}

// Utility function to highlight search matches
const highlightText = (text: string | null | undefined, searchTerm: string): React.ReactNode => {
	if (!text || !searchTerm) return text || "";

	// Split search term by spaces to highlight each word
	const searchWords = searchTerm
		.trim()
		.split(/\s+/)
		.filter((word) => word.length > 0);

	// Create a regex pattern that matches any of the search words
	const pattern = searchWords.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");

	if (!pattern) return text;

	const regex = new RegExp(`(${pattern})`, "gi");
	const parts = text.split(regex);

	return (
		<span>
			{parts.map((part, index) => {
				const isMatch = searchWords.some((word) => part.toLowerCase() === word.toLowerCase());

				return isMatch ? (
					<mark key={index} className="bg-yellow-300 px-0.5 rounded font-semibold">
						{part}
					</mark>
				) : (
					<span key={index}>{part}</span>
				);
			})}
		</span>
	);
};

export const AdminSearch: React.FC<AdminSearchProps> = ({
	className,
	onSearchChange,
	onSearchResults,
	onUpcomingEndDatesResults,
	dateFilter,
	dateFrom,
	dateTo,
	clearSearch,
	salespersonId,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [hasSearched, setHasSearched] = useState(false);

	const {
		data: searchResults,
		isLoading,
		refetch,
	} = useSalesContractsQuery(
		{
			search: searchTerm || undefined,
			per_page: 20,
			start_date: dateFrom,
			end_date: dateTo,
			...(salespersonId && { salesperson_id: salespersonId }),
		},
		{
			enabled: false, // Only search when user clicks search button
		},
	);

	// Separate query for upcoming end dates without date filtering
	const { data: upcomingEndDatesResults, refetch: refetchUpcomingEndDates } = useSalesContractsQuery(
		{
			search: searchTerm || undefined,
			per_page: 100, // Get more results for upcoming end dates
			status: "active", // Only active contracts
			...(salespersonId && { salesperson_id: salespersonId }),
		},
		{
			enabled: false, // Only search when user clicks search button
		},
	);

	const handleSearch = async () => {
		if (!searchTerm.trim()) return;
		setHasSearched(true);
		onSearchChange?.(searchTerm);

		// Fetch both search results and upcoming end dates results
		const [searchResult, upcomingEndDatesResult] = await Promise.all([refetch(), refetchUpcomingEndDates()]);

		onSearchResults?.(searchResult.data);
		onUpcomingEndDatesResults?.(upcomingEndDatesResult.data);
	};

	const handleClear = () => {
		setSearchTerm("");
		setHasSearched(false);
		onSearchChange?.("");
		onSearchResults?.(null);
		onUpcomingEndDatesResults?.(null);
	};

	// Listen for clear command from parent
	useEffect(() => {
		if (clearSearch) {
			handleClear();
		}
	}, [clearSearch]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};

	const handleExport = async (format: "csv" | "xlsx" | "pdf") => {
		if (!searchResults?.data || searchResults.data.length === 0) {
			return;
		}

		const { headers, rows } = prepareSalesContractDataForExport(searchResults.data);
		const filename = `search_results_${searchTerm.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}`;

		switch (format) {
			case "csv":
				exportToCSV({ headers, rows, filename });
				break;
			case "xlsx":
				exportToXLSX({ headers, rows, filename });
				break;
			case "pdf":
				await exportSalesContractsToPDF({ headers, rows, filename });
				break;
		}
	};

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	const columns = [
		{
			accessorKey: "project.name",
			label: "Project",
			cell: ({ data }: { data: Record<string, unknown> }) => {
				const projectName = data?.project?.name;
				const clientName = data?.project?.client?.company || data?.project?.client?.name;

				return (
					<div>
						<div className="font-medium">{highlightText(projectName, searchTerm) || "N/A"}</div>
						<div className="text-sm text-gray-500">
							{highlightText(clientName, searchTerm) || "N/A"} - ({data?.project?.category})
						</div>
					</div>
				);
			},
		},
		{
			accessorKey: "salesperson.name",
			label: "Salesperson",
			cell: ({ data }: { data: Record<string, unknown> }) => {
				const salespersonName = data?.salesperson?.name;
				const salespersonEmail = data?.salesperson?.email;

				if (!salespersonName && !salespersonEmail) return "N/A";

				return (
					<div>
						<div>{highlightText(salespersonName, searchTerm) || "N/A"}</div>
						{salespersonEmail && <div className="text-xs text-gray-500">{highlightText(salespersonEmail, searchTerm)}</div>}
					</div>
				);
			},
		},
		{
			accessorKey: "monthly_recurring_sales",
			label: "Monthly Sales",
			cell: ({ data }: { data: Record<string, unknown> }) => {
				return formatNumber(data?.monthly_recurring_sales);
			},
		},
		{
			accessorKey: "contract_amount",
			label: "Contract Amount",
			cell: ({ data }: { data: Record<string, unknown> }) => {
				return formatNumber(data?.contract_amount);
			},
		},
		{
			accessorKey: "invoice_total_amount",
			label: "Invoice Total",
			cell: ({ data }: { data: Record<string, unknown> }) => {
				const amount = Number(data?.invoice_total_amount) || 0;
				const safeAmount = Number.isNaN(amount) ? 0 : amount;
				return <div className="font-medium text-green-700">{formatCurrency(safeAmount)}</div>;
			},
		},
		{
			accessorKey: "effective_end_date",
			label: "Period",
			cell: ({ data }: { data: Record<string, unknown> }) => {
				if (!data?.end_date) return "N/A";
				return `${formatDate(data?.start_date, "PP")} - ${formatDate(data?.end_date, "PP")}`;
			},
		},
	];

	return (
		<Card className={className}>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<IconSearch className="h-5 w-5" />
					Admin Search
				</CardTitle>
				<div className="space-y-1">
					<p className="text-sm text-gray-600">Search by salesperson name, project title, or client name/company</p>
					{dateFilter && (
						<p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded inline-block">
							<strong>Date Filter Active:</strong> {dateFilter}
						</p>
					)}
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="flex gap-2">
					<Input
						placeholder="Search by salesperson, project, or client..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						onKeyPress={handleKeyPress}
						className="flex-1"
					/>
					<Button onClick={handleSearch} disabled={!searchTerm.trim() || isLoading} loading={isLoading}>
						<IconSearch className="h-4 w-4" />
					</Button>
					{searchTerm && (
						<Button variant="outline" onClick={handleClear}>
							<IconX className="h-4 w-4" />
						</Button>
					)}
				</div>

				{hasSearched && searchResults && (
					<div>
						<div className="mb-4 flex justify-between items-center">
							<p className="text-sm text-gray-600">
								Found {searchResults.data.length} results for "{searchTerm}"
							</p>
							{searchResults.data.length > 0 && (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant="outline" size="sm">
											<IconFileExport className="w-4 h-4 mr-2" />
											Export Results
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={() => handleExport("csv")}>
											<IconFileTypeCsv className="w-4 h-4 mr-2" />
											Export as CSV
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleExport("xlsx")}>
											<IconFileSpreadsheet className="w-4 h-4 mr-2" />
											Export as Excel
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleExport("pdf")}>
											<IconFileTypePdf className="w-4 h-4 mr-2" />
											Export as PDF
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</div>
						{searchResults.data.length > 0 ? (
							<DataTable
								columns={columns}
								data={searchResults.data}
								pagination={{
									pageCount: searchResults.meta.last_page,
									pageIndex: searchResults.meta.current_page - 1,
									pageSize: searchResults.meta.per_page,
									total: searchResults.meta.total,
								}}
							/>
						) : (
							<div className="text-center py-8">
								<p className="text-gray-500">No results found for "{searchTerm}"</p>
							</div>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
};
